import { makeObservable, observable } from 'mobx';

import {
    Dispatch,
    DropoffTask,
    EntityList,
    Job,
    PickupTask,
    RETURNED,
    Task,
    optional
} from '@vromo/models';
import { createModelSchema, primitive } from 'serializr';

export const QUOTE_COMPLETE = 'quote_complete';
export const OVERFLOW_CONFIRMED = 'overflow_confirmed';
export const OVERFLOW_CANCEL_CONFIRMED = 'overflow_cancel_confirmed';
export const OVERFLOW_CANCEL_FAILED = 'overflow_cancel_failed';
export const OVERFLOW_FAILED = 'overflow_failed';
export const RETURN_STATUSES = [OVERFLOW_CANCEL_CONFIRMED, OVERFLOW_CANCEL_FAILED, OVERFLOW_FAILED];

const WOLT_ID = 'Wolt';

export class CourierInfo {
    id: number | null = null;
    created: number | null = null;
    provider?: string | null = null;
    status?: string | null = null;
    details?: string | null = null;
    pickupEta?: number | null = null;
    dropoffEta?: number | null = null;

    constructor() {
        makeObservable(this, {
            id: observable,
            created: observable,
            provider: observable,
            status: observable,
            details: observable,
            pickupEta: observable,
            dropoffEta: observable
        });
    }
}

export const getReturnReason = (courierInfoList: CourierInfo[], dispatch: Dispatch): string => {
    const courierInfoItem =
        dispatch.type?.toLowerCase() === RETURNED
            ? courierInfoList?.find(
                  (ciItem) =>
                      dispatch?.audit?.user?.displayName
                          ?.toLowerCase()
                          .includes(ciItem.provider?.toLowerCase()) &&
                      RETURN_STATUSES.includes(ciItem?.status)
              )
            : null;
    const returnReason = courierInfoItem?.details || null;
    let jsonMessage = null;
    try {
        if (!!returnReason) jsonMessage = JSON.parse(returnReason);
    } catch (e) {
        console.log('Courier return reason not parsable. Showing raw string.', e);
    }
    return jsonMessage?.message || returnReason;
};

export const getETAsForJobFromCourierInfo = (
    courierInfo: EntityList<CourierInfo> | null,
    driverName?: string
): CourierInfo | null => {
    try {
        if (!courierInfo?.items.length) return null;
        const courierInfoEventList: CourierInfo[] | null =
            [...courierInfo?.items].reverse() || null;
        if (courierInfoEventList?.length) {
            const confirmedOverflowItems = courierInfoEventList?.filter(
                (item) => item.status === OVERFLOW_CONFIRMED
            );
            if (confirmedOverflowItems.length > 0) {
                let completedQuoteFromProvider = null;
                confirmedOverflowItems.forEach((confirmedOverflow) => {
                    const quoteCompleteItem = courierInfoEventList?.find(({ status, provider }) => {
                        if (!provider) return false;
                        const res =
                            status === QUOTE_COMPLETE && provider === confirmedOverflow.provider;
                        if (!driverName) {
                            return res;
                        }
                        if (driverName.includes(provider)) {
                            return res;
                        }
                        return false;
                    });
                    if (quoteCompleteItem) {
                        completedQuoteFromProvider = quoteCompleteItem;
                    }

                    // For WOLT, pickupETA comes from OVERFLOW_CONFIRMED item.
                    const isWolt = confirmedOverflow?.provider === WOLT_ID;
                    if (isWolt) {
                        completedQuoteFromProvider = confirmedOverflow;
                        if (quoteCompleteItem?.dropoffEta) {
                            completedQuoteFromProvider.dropoffEta = quoteCompleteItem.dropoffEta;
                        }
                        return;
                    }
                });
                return completedQuoteFromProvider;
            }
        }
    } catch (e) {
        console.log('Error fetching courier info:', e);
    }
    return null;
};

export const getETAForTask = (
    job: Job,
    ix: number,
    etas: { pickupEta: number | null; dropoffEta: number | null }
): number | null | undefined => {
    if (job.status === 'open') return null;
    const { pickupEta, dropoffEta } = etas;
    const task: Task = job.tasks!.items[ix]!;

    // Never show ETAs next to departure tasks.
    if (task?.zone?.type === 'departure') return null;

    // For job templates where type can be 'pickup' or 'dropoff'.
    if (task.type === PickupTask) return pickupEta;
    if (task.type === DropoffTask) return dropoffEta;

    // No template, so assume format.
    if (job.tasks?.items.length === 2) {
        return ix === 0 ? pickupEta : dropoffEta;
    }
    if (job.tasks?.items.length === 4) {
        if (ix === 0) return pickupEta;
        if (ix === 2) return dropoffEta;
    }
};

createModelSchema(CourierInfo, {
    id: optional(primitive()),
    created: optional(primitive()),

    provider: optional(primitive()),
    status: optional(primitive()),
    details: optional(primitive()),
    pickupEta: optional(primitive()),
    dropoffEta: optional(primitive())
});
